/**
 * MFE Loader Service
 * 
 * Dynamically loads Micro Frontend (MFE) components using Webpack Module Federation.
 * Handles remote entry loading, component resolution, and error handling.
 * 
 * Pattern adapted from lore-native's MfeLoader.ts but optimized for web.
 * 
 * Guardrails: one retry per scope per page session, cooldown before retry, structured failure log, perf logs gated.
 * @module services/mfe/MfeLoader
 */

import { importRemote } from '@module-federation/utilities';
import { getMfeUrl, getMfeProps, mfeRegistry } from '@/config/mfeRegistry';
import type { MfePropsContext } from '@/config/mfeRegistry';

/* We rely on @module-federation/utilities (^3.1.54) for bustRemoteEntryCache; it appends ?t= to remoteEntry URL when true. */

export interface LoadedMfe {
  Component: React.ComponentType<any>;
  props: any;
  store?: any; // Optional Redux store for MFEs that need it (FAQ, Streaks)
}

/** Error name thrown by @module-federation/utilities when remoteEntry.js fails to load (e.g. CORS/cache). */
const SCRIPT_EXTERNAL_LOAD_ERROR = 'ScriptExternalLoadError';

/** Cooldown (ms) before retrying a failed scope — prevents request storms when multiple MFEs fail. */
const RETRY_COOLDOWN_MS = 3000;

/** Gate verbose perf logs to avoid prod spam. Set REACT_APP_MFE_PERF_LOG=true to enable. */
const PERF_LOG_ENABLED = process.env.REACT_APP_MFE_PERF_LOG === 'true';

export class MfeLoader {
  private static loadedRemotes = new Set<string>();
  private static componentCache = new Map<string, LoadedMfe>();
  /** One retry per scope per page session. Cleared only on clearCache() / clearCacheAndContainers(). */
  private static scopesRetried = new Set<string>();

  /**
   * Get unique Module Federation scope names for all registered MFEs (for container cleanup).
   */
  private static getRemoteScopes(): string[] {
    const scopes = new Set<string>();
    Object.values(mfeRegistry).forEach((m) => scopes.add(m.config.scope));
    return Array.from(scopes);
  }

  /**
   * Remove webpack remote containers from window so next load fetches remoteEntry.js again.
   * Call after clearCache() on logout or retry to avoid reusing a broken container.
   */
  static clearRemoteContainers(): void {
    if (typeof window === 'undefined') return;
    const w = window as unknown as Record<string, unknown>;
    this.getRemoteScopes().forEach((scope) => {
      if (!w[scope]) return;
      if (PERF_LOG_ENABLED) console.log(`[MfeLoader] Removing remote container: ${scope}`);
      try {
        delete w[scope];
      } catch {
        try {
          (w as Record<string, unknown>)[scope] = undefined;
        } catch {
          console.warn(`[MfeLoader] Could not clear window.${scope} (non-configurable); next load may reuse container`);
        }
      }
    });
  }

  /**
   * Load MFE component dynamically
   * 
   * @param mfeName - Name of MFE from registry (e.g., 'Rewards', 'AccountCreation')
   * @param context - Props context (token, userId, callbacks)
   * @param options - bustRemoteEntryCache: true only on retry to avoid slow first loads
   * @returns Loaded component and props
   * 
   * @example
   * const { Component, props } = await MfeLoader.loadMfe('Rewards', {
   *   accessToken: token,
   *   refUserId: userId,
   *   onLogout: handleLogout,
   * });
   * return <Component {...props} />;
   */
  static async loadMfe(
    mfeName: string,
    context: MfePropsContext,
    options?: { bustRemoteEntryCache?: boolean }
  ): Promise<LoadedMfe> {
    const bustCache = options?.bustRemoteEntryCache ?? false;
    const t0 = typeof performance !== 'undefined' ? performance.now() : 0;

    try {
      // Return cached component if available (allows refresh via clearCache())
      if (this.componentCache.has(mfeName)) {
        if (PERF_LOG_ENABLED) console.log(`[MfeLoader] Using cached component for ${mfeName}`);
        return this.componentCache.get(mfeName)!;
      }

      if (PERF_LOG_ENABLED) {
        console.log(`[MfeLoader] Loading ${mfeName} (bustCache: ${bustCache})`);
      }

      // Get MFE configuration
      const mfeConfig = mfeRegistry[mfeName];
      if (!mfeConfig) {
        throw new Error(`MFE "${mfeName}" not found in registry`);
      }

      const { config } = mfeConfig;
      const remoteUrl = getMfeUrl(mfeName);

      if (PERF_LOG_ENABLED) {
        console.log(`[MfeLoader] ${mfeName} URL: ${remoteUrl}`);
      }

      // Load the remote module (first attempt: bustRemoteEntryCache false to use cache; retry uses true)
      const tImportStart = typeof performance !== 'undefined' ? performance.now() : 0;
      const module = await importRemote<any>(
        config.remoteEntryFileName != null
          ? {
              url: remoteUrl,
              scope: config.scope,
              module: config.module,
              remoteEntryFileName: config.remoteEntryFileName,
              bustRemoteEntryCache: bustCache,
            }
          : {
              url: remoteUrl,
              scope: config.scope,
              module: config.module,
              bustRemoteEntryCache: bustCache,
            }
      );
      const tImportEnd = typeof performance !== 'undefined' ? performance.now() : 0;
      if (PERF_LOG_ENABLED) {
        console.log(`[MfeLoader] ${mfeName} remoteEntry+init: ${(tImportEnd - tImportStart).toFixed(0)}ms`);
      }

      if (!module) {
        throw new Error(`Failed to load module ${config.module} from ${config.scope}`);
      }

      // Get the component (default export)
      const Component = module.default || module;

      if (!Component) {
        throw new Error(`No component found in module ${config.module}`);
      }

      // Check if module exports a Redux store (for FAQ/Streaks)
      const store = (module as any).store || null;

      // Get MFE-specific props
      const props = getMfeProps(mfeName, context);

      const tDone = typeof performance !== 'undefined' ? performance.now() : 0;
      if (PERF_LOG_ENABLED) {
        console.log(`[MfeLoader] ${mfeName} loaded in ${(tDone - t0).toFixed(0)}ms`);
      }

      this.loadedRemotes.add(mfeName);

      const loadedMfe: LoadedMfe = {
        Component,
        props,
        store, // Include store if available
      };

      // Cache the loaded component (can be cleared via clearCache() for refresh)
      this.componentCache.set(mfeName, loadedMfe);

      return loadedMfe;

    } catch (error) {
      const err = error as Error & { name?: string };
      const isScriptLoadError = err?.name === SCRIPT_EXTERNAL_LOAD_ERROR;
      const scope = mfeRegistry[mfeName]?.config?.scope;
      const remoteUrl = mfeRegistry[mfeName] ? getMfeUrl(mfeName) : '';
      const durationMs = typeof performance !== 'undefined' ? Math.round(performance.now() - t0) : 0;

      // Structured event for observability (one log per failed scope when we give up or before retry)
      const failureEvent = {
        event: 'MFE_LOAD_FAILED',
        scope: scope ?? undefined,
        remoteUrl,
        errorName: err?.name,
        errorMessage: err?.message?.slice(0, 200),
        bustRemoteEntryCache: bustCache,
        durationMs,
        willRetry: false,
      };

      // Circuit breaker: one retry per scope per page session, after cooldown to avoid request storms
      if (isScriptLoadError && scope && !this.scopesRetried.has(scope)) {
        this.scopesRetried.add(scope);
        this.componentCache.delete(mfeName);
        this.loadedRemotes.delete(mfeName);
        if (typeof window !== 'undefined') {
          const w = window as unknown as Record<string, unknown>;
          if (w[scope]) delete w[scope];
        }
        failureEvent.willRetry = true;
        console.warn('[MfeLoader]', failureEvent);
        console.warn(`[MfeLoader] ${mfeName} (${scope}): retrying after ${RETRY_COOLDOWN_MS}ms`);
        return new Promise<LoadedMfe>((resolve, reject) => {
          setTimeout(() => {
            this.loadMfe(mfeName, context, { bustRemoteEntryCache: true }).then(resolve).catch(reject);
          }, RETRY_COOLDOWN_MS);
        });
      }

      console.warn('[MfeLoader]', failureEvent);
      console.error(`❌ [MfeLoader] Failed to load ${mfeName}:`, error);
      throw error;
    }
  }

  /**
   * Check if MFE is already loaded
   * 
   * @param mfeName - Name of MFE
   * @returns true if MFE has been loaded
   */
  static isLoaded(mfeName: string): boolean {
    return this.loadedRemotes.has(mfeName);
  }

  /**
   * Clear in-memory caches only (for logout). Does NOT remove window[scope] containers,
   * so next load can reuse existing remoteEntry when possible (faster, avoids SVG/asset re-requests).
   */
  static clearCache(): void {
    if (PERF_LOG_ENABLED) console.log('[MfeLoader] Clearing in-memory cache');
    this.loadedRemotes.clear();
    this.componentCache.clear();
    this.scopesRetried.clear();
  }

  /**
   * Clear cache and remote container for a single MFE. Use when one panel refreshes
   * so other panels are not affected. Call from the loader effect after mount (not in click handler).
   * We clear in-memory cache always; we try to clear window[scope] so the next load fetches
   * remoteEntry again. Some environments throw on delete of window properties, so we use
   * assignment to undefined in try/catch and skip container clear if it throws.
   */
  static clearCacheAndContainersFor(mfeName: string): void {
    const mfeConfig = mfeRegistry[mfeName];
    if (!mfeConfig) return;
    const scope = mfeConfig.config.scope;
    this.componentCache.delete(mfeName);
    this.loadedRemotes.delete(mfeName);
    this.scopesRetried.delete(scope);
    if (typeof window !== 'undefined') {
      const w = window as unknown as Record<string, unknown>;
      if (w[scope]) {
        try {
          (w as Record<string, unknown>)[scope] = undefined;
          if (PERF_LOG_ENABLED) console.log(`[MfeLoader] Cleared container for ${mfeName}`);
        } catch {
          console.warn(`[MfeLoader] Could not clear window.${scope} (refresh will reuse existing container)`);
        }
      }
    }
  }

  /**
   * Full reset: in-memory caches + all remote containers. Use for "Reset MFEs" or Retry after error.
   * Next load will fetch remoteEntry.js again (slower; use sparingly).
   */
  static clearCacheAndContainers(): void {
    if (PERF_LOG_ENABLED) console.log('[MfeLoader] Full reset: cache and containers');
    this.loadedRemotes.clear();
    this.componentCache.clear();
    this.scopesRetried.clear();
    this.clearRemoteContainers();
  }
}

